// Servicio CMS para gestión de imágenes via backend
import { getAccessToken } from './api';
import API_CONFIG, { S3_CONFIG } from '../config/api';
import { saveCachedImages } from '../utils/cmsCache';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Consulta de imágenes compartida por todas las páginas (se reutiliza unos segundos).
let _metaPromesa = null;
let _metaHora = 0;
const META_TTL_MS = 15000;

// Extensiones soportadas en orden de prioridad
const SUPPORTED_EXTENSIONS = ['jpg', 'png', 'webp', 'gif'];

// Mapeo de secciones a nombres de archivo
const SECTION_FILE_NAMES = {
  hero_bg: 'hero_bg',
  banner_bg: 'banner_bg',
  carousel_1: 'carousel_1',
  carousel_2: 'carousel_2',
  carousel_3: 'carousel_3',
  carousel_4: 'carousel_4',
  carousel_5: 'carousel_5',
  event_1: 'event_1',
  event_2: 'event_2',
  event_3: 'event_3',
  event_4: 'event_4',
  page_hero_rules: 'page_hero_rules',
  page_hero_events: 'page_hero_events',
  page_hero_members: 'page_hero_members',
  page_hero_shop: 'page_hero_shop',
  page_hero_associates: 'page_hero_associates',
  page_hero_records: 'page_hero_records',
  page_hero_rankings: 'page_hero_rankings',
  page_hero_clasificacion: 'page_hero_clasificacion',
  page_hero_login: 'page_hero_login',
  page_hero_profile: 'page_hero_profile'
};

/**
 * Validar archivo antes de subir
 */
export const validateFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No se seleccionó ningún archivo' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Tipo de archivo no permitido. Use: JPG, PNG, GIF o WebP'
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `El archivo es demasiado grande (${sizeMB}MB). Máximo: 10MB`
    };
  }

  return { valid: true };
};

/**
 * Obtener extensión del archivo basado en tipo MIME
 */
const getFileExtension = (file) => {
  const mimeToExt = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp'
  };
  return mimeToExt[file.type] || 'jpg';
};

/**
 * Subir imagen al backend CMS
 * @param {File} file - Archivo de imagen
 * @param {string} section - Sección del CMS (hero_bg, carousel_1, etc.)
 * @returns {Promise<{ success: boolean, url: string, fileName: string }>}
 */
export const uploadImage = async (file, section) => {
  // Validar archivo
  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Validar sección
  const baseFileName = SECTION_FILE_NAMES[section];
  if (!baseFileName) {
    throw new Error(`Sección no válida: ${section}`);
  }

  // Obtener token de autenticación
  const token = getAccessToken();
  if (!token) {
    throw new Error('No has iniciado sesión. Por favor inicia sesión e intenta de nuevo.');
  }

  // Preparar FormData
  const formData = new FormData();
  formData.append('image', file);
  formData.append('folder', 'home');
  formData.append('filename', baseFileName);

  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/cms/images/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Error ${response.status}: ${response.statusText}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Error al subir imagen');
    }

    // La próxima consulta de imágenes debe traer la nueva versión
    _metaPromesa = null;

    // Agregar timestamp para evitar caché
    const urlWithTimestamp = `${data.data.image_url}?t=${Date.now()}`;

    return {
      success: true,
      url: urlWithTimestamp,
      fileName: `${baseFileName}.${getFileExtension(file)}`
    };
  } catch (error) {
    console.error('Error uploading image via CMS:', error);
    throw new Error(`Error al subir imagen: ${error.message}`);
  }
};

/**
 * Obtener URL de imagen del CMS
 * Las imágenes están en: public/home/{section}.{ext}
 * @param {string} section - Sección del CMS
 * @returns {string|null}
 */
export const getImageUrl = (section) => {
  const baseFileName = SECTION_FILE_NAMES[section];
  if (!baseFileName) return null;

  // Retorna URL base sin extensión (para compatibilidad)
  return `${S3_CONFIG.BASE_URL}home/${baseFileName}`;
};

/**
 * Obtener URL de imagen verificando existencia
 * @param {string} section - Sección del CMS
 * @returns {Promise<string|null>}
 */
export const getS3ImageUrl = async (section) => {
  const baseFileName = SECTION_FILE_NAMES[section];
  if (!baseFileName) return null;

  try {
    const meta = await getCmsImagesMetadata();
    if (Object.prototype.hasOwnProperty.call(meta, section)) return meta[section]?.url || null;
  } catch (e) {
    /* respaldo: búsqueda directa en S3 */
  }

  // Intentar cada extensión hasta encontrar una que exista
  for (const ext of SUPPORTED_EXTENSIONS) {
    const url = `${S3_CONFIG.BASE_URL}home/${baseFileName}.${ext}`;
    try {
      const response = await fetch(url, { method: 'HEAD' });
      if (response.ok) {
        return url;
      }
    } catch (e) {
      continue;
    }
  }
  return null;
};


/**
 * Metadata de las imágenes del CMS (home y portadas de páginas) desde el backend.
 * Las URLs traen ?v=<fecha de subida>: cambian cuando se reemplaza la imagen, así el
 * navegador no muestra una copia antigua de su caché.
 * @returns {Promise<Object>} { hero_bg: {url,...}|null, carousel_1: ..., page_hero_rules: ..., ... }
 */
export const getCmsImagesMetadata = () => {
  if (_metaPromesa && Date.now() - _metaHora < META_TTL_MS) return _metaPromesa;
  _metaHora = Date.now();
  _metaPromesa = fetch(`${API_CONFIG.BASE_URL}/api/cms/images/home`, { cache: 'no-store' })
    .then(async (response) => {
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Error al obtener imágenes');
      const meta = data.data || {};
      const paraCache = {};
      Object.keys(SECTION_FILE_NAMES).forEach((section) => {
        if (Object.prototype.hasOwnProperty.call(meta, section)) paraCache[section] = meta[section]?.url || null;
      });
      saveCachedImages(paraCache);
      return meta;
    })
    .catch((err) => {
      _metaPromesa = null;
      throw err;
    });
  return _metaPromesa;
};

/** URL (versionada) de una sección del CMS, o null si no tiene imagen. */
export const getCmsImageUrl = async (section) => {
  const meta = await getCmsImagesMetadata();
  return meta?.[section]?.url || null;
};

/**
 * Obtener imágenes del home desde el API backend (método eficiente)
 * @returns {Promise<Object>}
 */
export const getHomeImagesFromAPI = async () => {
  try {
    const meta = await getCmsImagesMetadata();
    const images = {
      hero_bg: meta.hero_bg?.url || null,
      banner_bg: meta.banner_bg?.url || null,
      carousel: [],
      events: [],
      _metadata: meta
    };
    for (let i = 1; i <= 5; i++) images.carousel.push({ id: i, image: meta[`carousel_${i}`]?.url || null });
    for (let i = 1; i <= 4; i++) images.events.push({ id: i, image: meta[`event_${i}`]?.url || null });
    return images;
  } catch (error) {
    console.error('Error fetching home images from API:', error);
    // Fallback al método anterior si el nuevo endpoint falla
    return getAllCMSImages();
  }
};

/**
 * Obtener todas las imágenes del CMS (método legacy con HEAD requests)
 * @deprecated Usar getHomeImagesFromAPI() para mejor rendimiento
 * @returns {Promise<Object>}
 */
export const getAllCMSImages = async () => {
  const sections = Object.keys(SECTION_FILE_NAMES);
  const images = {
    hero_bg: null,
    banner_bg: null,
    carousel: [],
    events: []
  };

  // Obtener todas las imágenes en paralelo
  const results = await Promise.all(
    sections.map(async (section) => ({
      section,
      url: await getS3ImageUrl(section)
    }))
  );

  // Organizar resultados
  results.forEach(({ section, url }) => {
    if (section === 'hero_bg') {
      images.hero_bg = url;
    } else if (section === 'banner_bg') {
      images.banner_bg = url;
    } else if (section.startsWith('carousel_')) {
      const index = parseInt(section.split('_')[1]) - 1;
      images.carousel[index] = { id: index + 1, image: url };
    } else if (section.startsWith('event_')) {
      const index = parseInt(section.split('_')[1]) - 1;
      images.events[index] = { id: index + 1, image: url };
    }
  });

  return images;
};

/**
 * Subir imagen a una ruta específica via backend (reemplaza uploadToPath de s3Service)
 * @param {File} file - Archivo de imagen
 * @param {string} folder - Carpeta destino (events, products, store, general, home)
 * @param {string} filename - Nombre sin extensión (ej: 'event_123')
 * @returns {Promise<{ url: string }>}
 */
export const uploadToPathViaBackend = async (file, folder, filename) => {
  const validation = validateFile(file);
  if (!validation.valid) throw new Error(validation.error);

  const token = getAccessToken();
  if (!token) throw new Error('No has iniciado sesión');

  const formData = new FormData();
  formData.append('image', file);
  formData.append('folder', folder);
  formData.append('filename', filename);

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), 60000) : null;

  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/cms/images/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
      signal: controller ? controller.signal : undefined,
    });

    const rawText = await response.text();
    let data = {};
    try {
      data = rawText ? JSON.parse(rawText) : {};
    } catch {
      data = { raw: rawText };
    }

    if (!response.ok || !data.success) {
      const backendMsg = data?.error || data?.message || data?.mensaje;
      const extra = !response.ok && rawText && !backendMsg ? `Respuesta: ${String(rawText).slice(0, 200)}` : '';
      throw new Error(backendMsg || `Error ${response.status}. ${extra}`.trim());
    }

    return { url: `${data.data.image_url}?t=${Date.now()}` };
  } catch (err) {
    if (String(err?.name || '').toLowerCase().includes('abort')) {
      throw new Error('Timeout subiendo imagen (60s). Reintenta o usa una imagen más liviana.');
    }
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const cmsService = {
  uploadImage,
  uploadToPathViaBackend,
  validateFile,
  getImageUrl,
  getS3ImageUrl,
  getAllCMSImages,
  getHomeImagesFromAPI,
  getCmsImagesMetadata,
  getCmsImageUrl
};

export default cmsService;

/**
 * Subir el logo de un club a S3 (carpeta public/clubs).
 * @param {File} file
 * @returns {Promise<string>} URL pública del logo
 */
export const uploadClubLogo = async (file) => {
  const validation = validateFile(file);
  if (!validation.valid) throw new Error(validation.error);

  const token = getAccessToken();
  if (!token) throw new Error('No has iniciado sesión. Por favor inicia sesión e intenta de nuevo.');

  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(`${API_CONFIG.BASE_URL}/api/cms/images/upload/clubs`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(data.error || `Error ${response.status} al subir el logo`);
  }
  return data.data.image_url;
};
